// @ts-nocheck
/**
 * Record of every Live Sheet link the organisation has shared. Expired and
 * revoked links stay listed, greyed out — the point is to know what went out.
 */
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Copy, Loader2, Ban, Mail } from "lucide-react";
import { toast } from "sonner";
import { shareSheetUrl } from "@/components/events/ShareSheetDialog";
import SendShareLinkForm from "@/components/events/SendShareLinkForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const isExpired = (t: any) =>
  t.expires_at && new Date(t.expires_at).getTime() < Date.now();

export default function SheetShareLinksPanel({ orgId }: { orgId?: string | null }) {
  const qc = useQueryClient();
  const [sendFor, setSendFor] = React.useState<any>(null);

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["livesheet-share-links", orgId ?? "all"],
    queryFn: async () => {
      let q = (supabase as any)
        .from("event_sheet_tokens")
        .select("*, events(name)")
        .eq("scope", "event")
        .order("created_at", { ascending: false });
      if (orgId) q = q.eq("organization_id", orgId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).filter((l: any) => l.label !== "__internal__");
    },
  });


  const { data: sends = [] } = useQuery({
    queryKey: ["livesheet-share-sends", orgId ?? "all"],
    queryFn: async () => {
      let q = (supabase as any)
        .from("event_sheet_token_access")
        .select("token_id, viewer_email, created_at")
        .eq("action", "sent")
        .order("created_at", { ascending: false })
        .limit(500);
      if (orgId) q = q.eq("organization_id", orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Latest send batch per token — same timestamp minute counts as one batch.
  const lastSendByToken = React.useMemo(() => {
    const map: Record<string, { at: string; emails: string[] }> = {};
    for (const s of sends as any[]) {
      const cur = map[s.token_id];
      if (!cur) {
        map[s.token_id] = { at: s.created_at, emails: [s.viewer_email] };
      } else if (
        Math.abs(new Date(cur.at).getTime() - new Date(s.created_at).getTime()) < 60000
      ) {
        cur.emails.push(s.viewer_email);
      }
    }
    return map;
  }, [sends]);

  const revoke = async (id: string) => {
    const { error } = await (supabase as any)
      .from("event_sheet_tokens")
      .update({ revoked: true, revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Link revoked — it stops working immediately.");
    qc.invalidateQueries({ queryKey: ["livesheet-share-links", orgId ?? "all"] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Live Sheet share links</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!isLoading && links.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No sheets shared yet. Use Share on any event's Live Sheet.
          </p>
        )}
        {links.map((t: any) => {
          const dead = t.revoked || isExpired(t);
          const emails = (t.allowed_emails ?? []).filter(Boolean);
          return (
            <div
              key={t.id}
              className={`flex flex-wrap items-start gap-3 rounded-md border p-3 text-sm ${
                dead ? "opacity-50" : ""
              }`}
            >
              <div className="min-w-[14rem] flex-1 space-y-1">
                <div className="font-medium">{t.label || "Untitled link"}</div>
                <div className="text-xs text-muted-foreground">
                  {t.events?.name ?? "Event"} · created{" "}
                  {new Date(t.created_at).toLocaleDateString()}
                </div>
                <div className="text-xs text-muted-foreground">
                  {emails.length ? (
                    <>Can open it: {emails.join(", ")}</>
                  ) : (
                    <>Can open it: anyone with the link</>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {lastSendByToken[t.id]
                    ? `Last sent ${new Date(
                        lastSendByToken[t.id].at,
                      ).toLocaleDateString()} to ${lastSendByToken[t.id].emails.join(", ")}`
                    : "Never emailed from here"}
                </div>
              </div>
              <Badge variant="outline">
                {t.permission === "edit" ? "View and edit" : "View only"}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {t.revoked
                  ? "Revoked"
                  : t.expires_at
                    ? `${isExpired(t) ? "Expired" : "Expires"} ${new Date(
                        t.expires_at
                      ).toLocaleDateString()}`
                    : "No expiry"}
              </span>

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSendFor(t)}>
                  <Mail className="mr-1 h-3.5 w-3.5" /> Send
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(shareSheetUrl(t.token));
                    toast.success("Copied.");
                  }}
                >
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
                {!t.revoked && (
                  <Button size="sm" variant="outline" onClick={() => revoke(t.id)}>
                    <Ban className="mr-1 h-3.5 w-3.5" /> Revoke
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!sendFor} onOpenChange={(o) => !o && setSendFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send this link</DialogTitle>
            <DialogDescription>
              Emails the existing link — it doesn't create a new one or change who can
              open it.
            </DialogDescription>
          </DialogHeader>
          {sendFor && (
            <div className="space-y-3">
              <Input
                readOnly
                value={shareSheetUrl(sendFor.token)}
                className="font-mono text-xs"
              />
              <SendShareLinkForm
                tokenId={sendFor.id}
                recipients={sendFor.allowed_emails ?? []}
                syncAllowedEmails={(sendFor.allowed_emails ?? []).length > 0}
                onSent={() => {
                  setSendFor(null);
                  qc.invalidateQueries({
                    queryKey: ["livesheet-share-links", orgId ?? "all"],
                  });
                  qc.invalidateQueries({
                    queryKey: ["livesheet-share-sends", orgId ?? "all"],
                  });
                }}
                onSkip={() => setSendFor(null)}
                skipLabel="Cancel"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
